import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Car,
  User,
  Phone,
  Mail,
  Camera,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Home,
  Loader2,
  X,
  Shield,
  Calendar,
  Clock,
  Image,
  PenTool,
  ExternalLink
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { compressImageFile } from '@/lib/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get current time in HH:MM format
const getCurrentTime = () => {
  const now = new Date();
  return now.toTimeString().slice(0, 5);
};

export default function LoanCarDetails() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const fileInputRef = useRef(null);
  const signatureRef = useRef(null);
  
  // Terms and Conditions modal
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Auto-reset countdown
  const [countdown, setCountdown] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    license_number: '',
    loan_car_rego: '',
    pickup_date: getTodayDate(),
    pickup_time: getCurrentTime(),
    signature: null,
    terms_accepted: false
  });
  
  const [licensePhoto, setLicensePhoto] = useState(null);
  const [licensePhotoPreview, setLicensePhotoPreview] = useState(null);

  // Reset form function
  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_address: '',
      license_number: '',
      loan_car_rego: '',
      pickup_date: getTodayDate(),
      pickup_time: getCurrentTime(),
      signature: null,
      terms_accepted: false
    });
    setLicensePhoto(null);
    setLicensePhotoPreview(null);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setSubmitted(false);
    setCountdown(null);
  };

  // Clear signature
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData(prev => ({ ...prev, signature: null }));
    }
  };

  // Save signature
  const saveSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureData = signatureRef.current.toDataURL('image/png');
      setFormData(prev => ({ ...prev, signature: signatureData }));
      toast.success('Signature captured');
    } else {
      toast.error('Please draw your signature first');
    }
  };

  // Auto-reset after submission
  useEffect(() => {
    if (submitted && !error) {
      setCountdown(10);
    }
  }, [submitted, error]);

  useEffect(() => {
    if (countdown === null) return;
    
    if (countdown <= 0) {
      resetForm();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // Fetch token info on mount
  useEffect(() => {
    const fetchTokenInfo = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bookings/public/loan-car-token/${token}`);
        if (response.ok) {
          const data = await response.json();
          setTokenInfo(data);
          
          // Check if already submitted
          if (data.submitted) {
            setSubmitted(true);
          } else {
            // Pre-fill customer info if available
            setFormData(prev => ({
              ...prev,
              customer_name: data.customer_name || '',
              customer_phone: data.customer_phone || ''
            }));
          }
        } else if (response.status === 404) {
          setError('This link has expired or is invalid.');
        } else {
          setError('Something went wrong. Please try again.');
        }
      } catch (err) {
        console.error('Failed to fetch token info:', err);
        setError('Unable to connect. Please check your internet connection.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchTokenInfo();
    }
  }, [token]);

  // Handle license photo capture - allow both camera and gallery
  const [compressingPhoto, setCompressingPhoto] = useState(false);
  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file size (max 15MB raw — we compress client-side anyway).
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Photo must be less than 15MB');
      return;
    }
    setCompressingPhoto(true);
    try {
      const compressed = await compressImageFile(file);
      setLicensePhoto(compressed);
      setLicensePhotoPreview(compressed);
      toast.success('Photo uploaded!');
    } catch (err) {
      console.error('Photo compression failed:', err);
      toast.error('Could not process photo. Please try a different one.');
    } finally {
      setCompressingPhoto(false);
    }
  };

  // Remove photo
  const removePhoto = () => {
    setLicensePhoto(null);
    setLicensePhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customer_name.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.customer_phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.license_number.trim()) {
      toast.error('Please enter your license number');
      return;
    }
    if (!formData.signature) {
      toast.error('Please draw your signature to confirm agreement');
      return;
    }
    if (!formData.terms_accepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setSubmitting(true);
    try {
      // Combine pickup date and time
      const pickupDatetime = formData.pickup_date && formData.pickup_time 
        ? `${formData.pickup_date}T${formData.pickup_time}:00`
        : null;

      const response = await fetch(`${BACKEND_URL}/api/bookings/public/loan-car-token/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formData.customer_name.trim(),
          customer_phone: formData.customer_phone.trim(),
          customer_email: formData.customer_email.trim() || null,
          customer_address: formData.customer_address.trim() || null,
          license_number: formData.license_number.trim(),
          license_image: licensePhoto,
          signature: formData.signature,
          loan_car_rego: formData.loan_car_rego.trim().toUpperCase() || null,
          pickup_datetime: pickupDatetime,
          terms_accepted: formData.terms_accepted
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Details submitted successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Submit failed:', err);
      toast.error('Failed to submit. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Toaster position="top-center" richColors />
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Invalid</h1>
          <p className="text-slate-600 mb-4">{error}</p>
          <p className="text-sm text-slate-500">
            Please ask the workshop for a new QR code.
          </p>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">All Done!</h1>
          <p className="text-slate-600 mb-6">
            Your loan car details have been submitted successfully.
          </p>
          
          <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Name:</span>
              <span className="font-medium">{formData.customer_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">License:</span>
              <span className="font-medium">{formData.license_number}</span>
            </div>
            {formData.loan_car_rego && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Loan Car:</span>
                <span className="font-medium font-mono">{formData.loan_car_rego.toUpperCase()}</span>
              </div>
            )}
            {formData.pickup_date && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Pickup:</span>
                <span className="font-medium">
                  {new Date(formData.pickup_date).toLocaleDateString('en-AU', { 
                    weekday: 'short', day: 'numeric', month: 'short' 
                  })} at {formData.pickup_time}
                </span>
              </div>
            )}
          </div>
          
          <p className="text-sm text-slate-500 mt-6">
            The workshop will have the loan car ready for you.
          </p>
          
          {/* Auto-reset countdown */}
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              This screen will reset in <span className="font-bold text-blue-600">{countdown}</span> seconds
            </p>
            <Button 
              variant="outline" 
              onClick={resetForm}
              className="mt-3"
            >
              Submit Another Entry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Loan Car Details</h1>
          <p className="text-slate-600 mt-1 text-sm">
            Please provide your details for the loan car
          </p>
        </div>

        {/* Vehicle Info Banner */}
        {tokenInfo?.vehicle_rego && (
          <Card className="mb-4 p-4 bg-blue-600 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Car className="h-5 w-5" />
              </div>
              <div>
                <p className="text-blue-100 text-xs">Your Vehicle</p>
                <p className="text-xl font-bold font-mono">{tokenInfo.vehicle_rego}</p>
              </div>
            </div>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          {/* Loan Car Details Section */}
          <Card className="mb-4 p-5">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              Loan Car Details
            </h2>
            
            <div className="space-y-4">
              {/* Loan Car Rego */}
              <div>
                <Label htmlFor="loan_car_rego" className="text-sm">Loan Car Registration (if known)</Label>
                <div className="relative mt-1">
                  <Car className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="loan_car_rego"
                    value={formData.loan_car_rego}
                    onChange={(e) => setFormData(prev => ({ ...prev, loan_car_rego: e.target.value.toUpperCase() }))}
                    placeholder="e.g. ABC123"
                    className="h-12 pl-10 uppercase font-mono"
                    maxLength={7}
                    data-testid="loan-car-rego-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Leave blank if not assigned yet</p>
              </div>

              {/* Pickup Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pickup_date" className="text-sm">Pickup Date</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="pickup_date"
                      type="date"
                      value={formData.pickup_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, pickup_date: e.target.value }))}
                      className="h-12 pl-10"
                      data-testid="pickup-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pickup_time" className="text-sm">Pickup Time</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="pickup_time"
                      type="time"
                      value={formData.pickup_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, pickup_time: e.target.value }))}
                      className="h-12 pl-10"
                      data-testid="pickup-time-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* License Photo Section */}
          <Card className="mb-4 p-5">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Driver's License
            </h2>
            
            {/* Photo Upload - Camera OR Gallery */}
            <div className="mb-4">
              <Label className="text-sm text-slate-600 mb-2 block">License Photo</Label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              
              {licensePhotoPreview ? (
                <div className="relative">
                  <img 
                    src={licensePhotoPreview} 
                    alt="License" 
                    className="w-full h-40 object-cover rounded-lg border-2 border-green-300"
                    data-testid="loan-details-license-preview"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Photo Added
                  </div>
                </div>
              ) : compressingPhoto ? (
                <div className="w-full h-24 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50/50 flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
                  <span className="text-blue-700 font-medium">Optimising photo...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Camera option */}
                  <button
                    type="button"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.setAttribute('capture', 'environment');
                        fileInputRef.current.click();
                      }
                    }}
                    className="w-full h-24 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50/50 hover:bg-blue-100/50 transition-colors flex items-center justify-center gap-3"
                    data-testid="capture-license-btn"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Camera className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-blue-700 block">Take Photo</span>
                      <span className="text-xs text-slate-500">Use camera to capture</span>
                    </div>
                  </button>
                  
                  {/* Gallery option */}
                  <button
                    type="button"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.removeAttribute('capture');
                        fileInputRef.current.click();
                      }
                    }}
                    className="w-full h-24 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors flex items-center justify-center gap-3"
                    data-testid="select-photo-btn"
                  >
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <Image className="h-6 w-6 text-slate-600" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-slate-700 block">Choose from Gallery</span>
                      <span className="text-xs text-slate-500">Select existing photo</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
            
            {/* License Number */}
            <div>
              <Label htmlFor="license_number" className="text-sm">License Number *</Label>
              <div className="relative mt-1">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value }))}
                  placeholder="Enter license number"
                  className="h-12 pl-10"
                  data-testid="license-number-input"
                />
              </div>
            </div>
          </Card>

          {/* Personal Details */}
          <Card className="mb-4 p-5">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Your Details
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_name" className="text-sm">Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                    placeholder="Your full name"
                    className="h-12 pl-10"
                    data-testid="customer-name-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="customer_phone" className="text-sm">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                    placeholder="0400 000 000"
                    className="h-12 pl-10"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="customer_email" className="text-sm">Email (Optional)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="your@email.com"
                    className="h-12 pl-10"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="customer_address" className="text-sm">Home Address (Optional)</Label>
                <div className="relative mt-1">
                  <Home className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    id="customer_address"
                    value={formData.customer_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                    placeholder="Your home address"
                    className="pl-10 min-h-[80px]"
                    data-testid="customer-address-input"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Signature Section */}
          <Card className="mb-6 p-5">
            <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <PenTool className="h-5 w-5 text-blue-600" />
              Your Signature
            </h3>
            
            <p className="text-sm text-slate-500 mb-3">
              Please sign below to confirm you agree to the terms and conditions
            </p>
            
            <div className="border-2 border-slate-200 rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'w-full h-40',
                  style: { width: '100%', height: '160px' }
                }}
                backgroundColor="white"
              />
            </div>
            
            <div className="flex gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={saveSignature}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Confirm Signature
              </Button>
            </div>
            
            {formData.signature && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">Signature captured</span>
              </div>
            )}
          </Card>

          {/* Terms & Conditions */}
          <Card className="mb-6 p-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms_accepted"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <div>
                <Label htmlFor="terms_accepted" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                  I acknowledge that I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-blue-600 underline hover:text-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    Courtesy Vehicle Terms & Conditions
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </Label>
                <p className="text-xs text-slate-400 mt-1">
                  Click the link above to read the full terms before signing
                </p>
              </div>
            </div>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting || compressingPhoto || !formData.signature || !formData.terms_accepted}
            className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 shadow-lg"
            data-testid="submit-details-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Shield className="h-5 w-5 mr-2" />
                Submit Details
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Your details are securely transmitted
        </p>
      </div>

      {/* Terms and Conditions Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              Courtesy / Loan Vehicle Agreement
            </DialogTitle>
          </DialogHeader>
          
          <div className="prose prose-sm max-w-none text-slate-700 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="font-semibold text-blue-900 mb-1">Hoppers Service Centre & Mechanical Repairs</p>
              <p className="text-sm text-blue-800">8 Dunlop Road, Hoppers Crossing VIC 3029</p>
              <p className="text-sm text-blue-800">Phone: 9749 0244</p>
            </div>
            
            <p className="text-slate-600">
              This Courtesy Vehicle Agreement ("Agreement") governs the use of a loan vehicle provided by 
              Hoppers Service Centre & Mechanical Repairs ("the Workshop") to the customer ("the Driver").
            </p>
            
            <p className="font-medium text-slate-800">
              By accepting possession of the courtesy vehicle, the Driver agrees to the following terms and conditions.
            </p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">1. DRIVER ELIGIBILITY</h3>
            <p>The Driver must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Hold a current and valid driver licence</li>
              <li>Be legally permitted to drive in Australia</li>
              <li>Provide a valid licence for verification</li>
              <li>Be at least 21 years of age (recommended clause)</li>
            </ul>
            <p>The courtesy vehicle may only be driven by the authorised driver listed in this agreement unless written approval is given by the Workshop.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">2. PURPOSE OF LOAN VEHICLE</h3>
            <p>The courtesy vehicle is provided solely as a temporary convenience vehicle while the Driver's vehicle is being serviced or repaired by the Workshop.</p>
            <p>The vehicle must be returned immediately upon request or upon completion of repairs.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">3. INSURANCE AND EXCESS LIABILITY</h3>
            <p>The courtesy vehicle is insured; however the Driver agrees to the following insurance excess liability in the event of any accident, damage, or insurance claim occurring during the loan period.</p>
            
            <div className="bg-amber-50 p-4 rounded-lg mt-3">
              <p className="font-semibold text-amber-900">Australian Driver Licence Holders</p>
              <p className="text-amber-800">The Driver agrees to pay a maximum insurance excess of <strong>$500</strong> for any damage, accident, or claim involving the vehicle while in their possession or control.</p>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg mt-3">
              <p className="font-semibold text-red-900">International Driver Licence Holders</p>
              <p className="text-red-800">Drivers using an international driver licence or overseas licence agree to pay a maximum insurance excess of <strong>$2,000</strong> for any damage, accident, or claim involving the vehicle.</p>
            </div>
            
            <p>The excess applies regardless of fault and is payable immediately upon request.</p>
            <p>If damage is not covered by insurance, the Driver may be liable for the full cost of repairs.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">4. DRIVER RESPONSIBILITY</h3>
            <p>The Driver accepts full responsibility for the vehicle during the loan period including but not limited to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Damage to the vehicle</li>
              <li>Negligent driving</li>
              <li>Loss of keys</li>
              <li>Interior damage</li>
              <li>Tyre or wheel damage</li>
              <li>Excessive cleaning</li>
            </ul>

            <h3 className="text-lg font-bold text-slate-900 mt-6">5. TRAFFIC INFRINGEMENTS AND TOLLS</h3>
            <p>The Driver is fully responsible for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Traffic infringements</li>
              <li>Speed camera offences</li>
              <li>Red light camera offences</li>
              <li>Parking fines</li>
              <li>Toll charges</li>
            </ul>
            <p>The Driver authorises the Workshop to transfer liability for fines to the Driver and to provide the Driver's personal details to relevant authorities if required.</p>
            <p>The Driver agrees to pay any applicable administration fees associated with the transfer of infringement notices.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">6. PROHIBITED USE</h3>
            <p>The courtesy vehicle must NOT be used for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Commercial purposes (Uber, rideshare, delivery services, courier work)</li>
              <li>Driving under the influence of alcohol or drugs</li>
              <li>Illegal activity</li>
              <li>Motor racing or reckless driving</li>
              <li>Towing or carrying heavy loads</li>
              <li>Off-road driving</li>
              <li>Driving outside Victoria without prior approval</li>
            </ul>
            <p className="font-medium text-red-700">Any breach of these conditions may void insurance coverage and result in full liability for damages.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">7. ACCIDENT PROCEDURE</h3>
            <p>In the event of an accident or incident involving the vehicle, the Driver must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Notify the Workshop immediately</li>
              <li>Provide full details of the incident</li>
              <li>Report the incident to police where required</li>
              <li>Cooperate fully with insurance investigations</li>
            </ul>
            <p className="font-medium text-amber-700">Failure to report an incident immediately may result in additional liability.</p>

            <h3 className="text-lg font-bold text-slate-900 mt-6">8-14. ADDITIONAL TERMS</h3>
            <p>The vehicle must be returned in the same condition. The Workshop is not responsible for personal property left in the vehicle. The Driver agrees to indemnify the Workshop from any claims arising from vehicle use.</p>

            <div className="bg-slate-100 p-4 rounded-lg mt-6">
              <p className="text-center text-slate-700 font-medium">
                By signing below, you confirm that you have read and agree to all terms and conditions outlined in this agreement.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button onClick={() => setShowTermsModal(false)} className="bg-blue-600 hover:bg-blue-700">
              I've Read the Terms - Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
